import { useState, useRef, useEffect } from 'react';
import {
  CheckCircleIcon,
  ArrowUturnLeftIcon,
  ArrowLeftIcon,
  InformationCircleIcon,
  TagIcon,
  ChevronDownIcon,
  XMarkIcon,
  PlusIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { Avatar, Badge } from '../../../components';
import { Conversation, ConversationPriority } from '../../../types';
import { useConversations } from '../../conversations/hooks/useConversations';
import { useFeedback } from '../../conversations/hooks/useFeedback';
import { useUIStore } from '../../../stores';
import { useIsDesktop } from '../../../lib/hooks/useMediaQuery';
import CallCustomerButton from '../../voice/components/CallCustomerButton';
import WhatsAppNumberBadge from '../../conversations/components/WhatsAppNumberBadge';

interface ChatHeaderProps {
  conversation: Conversation;
}

const PRIORITY_OPTIONS: { value: ConversationPriority; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: 'text-ink-3' },
  { value: 'normal', label: 'Normal', color: 'text-brand' },
  { value: 'high', label: 'High', color: 'text-warning' },
  { value: 'urgent', label: 'Urgent', color: 'text-danger' },
];

export default function ChatHeader({ conversation }: ChatHeaderProps) {
  const { resolveConversation, returnToBot, updatePriority, updateLabels } = useConversations();
  const { setMobileView, copilotPanelOpen, toggleCopilotPanel } = useUIStore();
  const isDesktop = useIsDesktop();
  // Live CSAT score for the pill — useFeedback keeps it in sync via the
  // feedback:recorded socket event so the header updates the moment the
  // customer taps a star.
  const { latestRated } = useFeedback(conversation.id);
  const [showPriority, setShowPriority] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const priorityRef = useRef<HTMLDivElement>(null);
  const labelsRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (priorityRef.current && !priorityRef.current.contains(e.target as Node)) {
        setShowPriority(false);
      }
      if (labelsRef.current && !labelsRef.current.contains(e.target as Node)) {
        setShowLabels(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const getStatusBadge = () => {
    switch (conversation.state) {
      case 'bot_active':
        return <Badge variant="info">AI Handling</Badge>;
      case 'staff_active':
        return <Badge variant="success">Active</Badge>;
      case 'awaiting_staff':
        return <Badge variant="warning">Waiting</Badge>;
      case 'resolved':
        return <Badge variant="default">Resolved</Badge>;
      default:
        return null;
    }
  };

  const currentPriority = PRIORITY_OPTIONS.find((p) => p.value === conversation.priority) || PRIORITY_OPTIONS[1];

  const handleAddLabel = () => {
    const label = newLabel.trim();
    if (!label) return;
    const existing = conversation.labels || [];
    if (!existing.includes(label)) {
      updateLabels({ id: conversation.id, labels: [...existing, label] });
    }
    setNewLabel('');
  };

  const handleRemoveLabel = (label: string) => {
    const updated = (conversation.labels || []).filter((l) => l !== label);
    updateLabels({ id: conversation.id, labels: updated });
  };

  const canReturnToBot = conversation.state === 'staff_active' || conversation.state === 'awaiting_staff';

  return (
    // The header is its own size container: its button labels show only when
    // the header itself is wide enough, not whenever the viewport is. On
    // desktop the thread sits between two 320px panels, so a wide screen can
    // still leave it narrow, and the labels pushed Customer Info off-screen.
    <div className="border-b border-line bg-surface px-3 py-2 [container-type:inline-size] sm:px-6 sm:py-3">
      {/* Top row: customer info + actions */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
          {/* Mobile back button */}
          {!isDesktop && (
            <button
              onClick={() => setMobileView('list')}
              className="rounded-lg p-1.5 text-ink-2 hover:bg-black/[0.04]"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
          )}
          <Avatar
            name={conversation.customerName}
            src={conversation.customerAvatar}
            size="lg"
            className="flex-shrink-0"
          />
          <div className="min-w-0">
            <h2 className="truncate font-display text-base font-semibold text-ink sm:text-lg">
              {conversation.customerName}
            </h2>
            <p className="hidden truncate text-sm text-ink-3 sm:block">
              {conversation.channel === 'web'
                ? conversation.customerEmail || 'Web Chat'
                : conversation.customerPhone}
            </p>
          </div>
          {getStatusBadge()}
          <WhatsAppNumberBadge conversation={conversation} className="text-xs" />
          {latestRated?.score && (
            <span
              className="inline-flex flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-warning/50 bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning"
              title={`Customer rated ${latestRated.score}/5${latestRated.respondedAt ? ` on ${new Date(latestRated.respondedAt).toLocaleString()}` : ''}`}
            >
              <StarSolid className="h-3 w-3 text-warning" />
              Rated {latestRated.score}/5
            </span>
          )}
        </div>

        <div className="flex flex-shrink-0 items-center gap-1 sm:gap-2">
          {/* Return to Bot */}
          {canReturnToBot && (
            <button
              onClick={() => returnToBot(conversation.id)}
              className="flex items-center gap-1.5 rounded-pill p-1.5 text-sm font-medium text-purple-600 hover:bg-purple-500/10 [@container(min-width:48rem)]:px-3"
              title="Return to AI bot"
            >
              <ArrowUturnLeftIcon className="h-4 w-4" />
              <span className="hidden [@container(min-width:48rem)]:inline">Return to Bot</span>
            </button>
          )}

          {/* Call customer (outbound) — only shown when we have a phone number */}
          {conversation.customerPhone && (
            <CallCustomerButton
              customerPhone={conversation.customerPhone}
              conversationId={conversation.id}
              customerName={conversation.customerName}
              className="sm:px-1.5 [@container(min-width:48rem)]:px-3"
              labelClassName="hidden [@container(min-width:48rem)]:inline"
            />
          )}

          {/* AI Copilot (private staff ↔ AI assistant drawer) */}
          <button
            onClick={toggleCopilotPanel}
            className={`flex items-center gap-1.5 rounded-pill p-1.5 text-sm font-medium hover:bg-brand/10 [@container(min-width:48rem)]:px-3 ${
              copilotPanelOpen ? 'bg-brand/10 text-brand' : 'text-brand'
            }`}
            title="Ask the AI privately (not visible to the customer)"
          >
            <SparklesIcon className="h-4 w-4" />
            <span className="hidden [@container(min-width:48rem)]:inline">Copilot</span>
          </button>

          {/* Resolve */}
          {conversation.state !== 'resolved' && (
            <button
              onClick={() => resolveConversation(conversation.id)}
              className="flex items-center gap-1.5 rounded-pill p-1.5 text-sm font-medium text-success hover:bg-success/10 [@container(min-width:48rem)]:px-3"
              title="Resolve conversation"
            >
              <CheckCircleIcon className="h-4 w-4" />
              <span className="hidden [@container(min-width:48rem)]:inline">Resolve</span>
            </button>
          )}

          {/* Priority Dropdown */}
          <div className="relative" ref={priorityRef}>
            <button
              onClick={() => setShowPriority(!showPriority)}
              className={`flex items-center gap-1 rounded-pill border border-line px-2.5 py-1.5 text-sm font-medium ${currentPriority.color} hover:bg-black/[0.04]`}
            >
              {currentPriority.label}
              <ChevronDownIcon className="h-3.5 w-3.5" />
            </button>
            {showPriority && (
              <div className="absolute right-0 z-20 mt-1 w-32 rounded-card border border-line bg-surface py-1 shadow-m">
                {PRIORITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      updatePriority({ id: conversation.id, priority: opt.value });
                      setShowPriority(false);
                    }}
                    className={`flex w-full items-center px-3 py-1.5 text-left text-sm hover:bg-black/[0.04] ${opt.color} ${
                      opt.value === conversation.priority ? 'bg-black/[0.04] font-medium' : ''
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Customer info button (mobile only) */}
          {!isDesktop && (
            <button
              onClick={() => setMobileView('customer')}
              className="rounded-lg p-1.5 text-ink-2 hover:bg-black/[0.04]"
              title="Customer info"
            >
              <InformationCircleIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Bottom row: labels */}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <TagIcon className="h-4 w-4 text-ink-3" />
        {(conversation.labels || []).map((label) => (
          <span
            key={label}
            className="inline-flex items-center gap-1 rounded-full bg-surface-2 px-2 py-0.5 text-xs font-medium text-ink-2"
          >
            {label}
            <button
              onClick={() => handleRemoveLabel(label)}
              className="rounded-full p-0.5 hover:bg-black/[0.06]"
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </span>
        ))}

        {/* Add label */}
        <div className="relative" ref={labelsRef}>
          {showLabels ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAddLabel();
              }}
              className="flex items-center gap-1"
            >
              <input
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Add label"
                autoFocus
                className="w-24 rounded-xl border border-line bg-surface px-2 py-0.5 text-xs text-ink focus:border-brand focus:outline-none"
                onBlur={() => {
                  if (!newLabel.trim()) setShowLabels(false);
                }}
              />
            </form>
          ) : (
            <button
              onClick={() => setShowLabels(true)}
              className="flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs text-ink-3 hover:bg-black/[0.04] hover:text-ink-2"
            >
              <PlusIcon className="h-3 w-3" />
              Label
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

